import { useMemo } from 'react';
import {
    Background,
    BackgroundVariant,
    Controls,
    type Edge,
    MarkerType,
    type Node,
    ReactFlow,
} from '@xyflow/react';

import { Card } from '@/components/atoms/Card';
import { useChartTheme } from '@/components/charts/theme';
import type { FishGroupLineage } from '@/domain/fishGroups';

import LineageGraphNode, { type LineageNodeData } from './LineageGraphNode';
import LineageLegend from './LineageLegend';
import { colorForOperationType } from './lineageEdgeColors';
import { buildLineageEdges, layoutLineageNodes } from './lineageLayout';

import '@xyflow/react/dist/style.css';
import styles from './LineageGraph.module.scss';

interface Props {
    lineage: FishGroupLineage;
    onNavigate: (fishGroupId: number) => void;
}

const nodeTypes = { fishGroup: LineageGraphNode };

export default function LineageGraph(props: Props) {
    const { lineage, onNavigate } = props;
    // React Flow styles its controls, dots and handles from its own colour mode, and the edges
    // take the palette colours as this theme draws them.
    const theme = useChartTheme();

    const nodes = useMemo<Node<LineageNodeData>[]>(() => {
        return layoutLineageNodes(lineage.graph.nodes).map((p) => ({
            id: p.id,
            type: 'fishGroup',
            position: p.position,
            data: {
                node: p.node,
                isRoot: p.node.id === lineage.rootId,
                onNavigate,
            },
            // Disable default drag/select handlers — the lineage view is a
            // read-only diagram, not an editable canvas.
            draggable: false,
            selectable: false,
        }));
    }, [lineage, onNavigate]);

    const edges = useMemo<Edge[]>(() => {
        // buildLineageEdges preserves input order, so we can pair its output
        // with lineage.graph.edges by index to recover each edge's transfer.
        const layoutEdges = buildLineageEdges(lineage.graph.edges);
        return layoutEdges.map((e, i) => {
            const domainEdge = lineage.graph.edges[i];
            const color = theme.color(colorForOperationType(domainEdge.transfer.operationType));
            // A reverted operation's transfers stay in the record; dashed and faded
            // so the annulled branch reads as such.
            const reverted = domainEdge.transfer.revertedByOperationId !== null;
            return {
                ...e,
                type: 'smoothstep',
                style: {
                    stroke: color,
                    strokeWidth: 2,
                    ...(reverted ? { strokeDasharray: '6 4', opacity: 0.5 } : {}),
                },
                markerEnd: { type: MarkerType.ArrowClosed, color },
            };
        });
    }, [lineage.graph.edges, theme]);

    return (
        <div className={styles.graphContainer}>
            <LineageLegend edges={lineage.graph.edges} />
            <Card variant="subtle" radius="sm" padding="none" className={styles.graphWrapper}>
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    nodeTypes={nodeTypes}
                    colorMode={theme.mode}
                    nodesDraggable={false}
                    nodesConnectable={false}
                    edgesFocusable={false}
                    fitView
                    fitViewOptions={{ padding: 0.2, maxZoom: 1.2 }}
                    proOptions={{ hideAttribution: true }}>
                    <Background variant={BackgroundVariant.Dots} gap={16} size={1} />
                    <Controls showInteractive={false} />
                </ReactFlow>
            </Card>
        </div>
    );
}
